import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { ensureAuthMe, useTabFocusAutoLogin } from 'shared/hooks';
import { CentredLayoutWithHeader } from 'shared/layouts';
import { routes } from 'shared/routes';
import { consumeExplicitLogout, isOAuthAuthorizeRedirect } from 'shared/services';

import { loginSearchSchema } from 'features/auth/Login';
import { useGetStartedStore } from 'features/get-started/hooks';

export const Route = createFileRoute('/auth/_auth')({
  // Cookie-based auto-login for every page under this layout: the in-memory
  // access token is always empty after a reload even when the httpOnly cookie
  // is still valid, so `ensureAuthMe` triggers the cookie-based refresh via the
  // auth middleware. A valid session bounces the visitor into the app; a 401
  // falls through to the requested auth page (`handleSessionExpired` no-ops on
  // /auth routes, so anonymous visitors see no toast and no redirect loop).
  beforeLoad: async ({ context, location }) => {
    useGetStartedStore.getState().reset();

    // An explicit logout just navigated here — the fire-and-forget server-side
    // revocation may not have landed yet, and refreshing now could resurrect
    // the session being logged out. Skip the auto-login attempt once.
    if (consumeExplicitLogout()) {
      return;
    }

    try {
      await ensureAuthMe(context.queryClient);
    } catch {
      // Not authenticated — show the requested auth page.
      return;
    }
    // safeParse (not parse) so this layout — which also wraps forgot-/reset-
    // password, whose schemas don't validate `redirect` — can never throw a
    // route error over the param; fall back to locations instead.
    const parsedSearch = loginSearchSchema.safeParse(location.search);
    const redirectTo = parsedSearch.success ? parsedSearch.data.redirect : undefined;

    // Backend-served OAuth authorize target: a relative href is a soft SPA nav
    // (router-core only hard-navigates absolute hrefs), so force a document
    // load; `replace` keeps `/auth/login` off the back-button stack.
    if (redirectTo && isOAuthAuthorizeRedirect(redirectTo)) {
      throw redirect({ href: redirectTo, reloadDocument: true, replace: true });
    }

    // `replace` keeps `/auth/login` off the back stack when a valid session
    // auto-bounces an already-authenticated visitor into the app.
    throw redirect({ href: redirectTo ?? routes.locations(), replace: true });
  },
  component: AuthLayout
});

function AuthLayout() {
  useTabFocusAutoLogin();

  return (
    <CentredLayoutWithHeader dataHook="auth-layout">
      <Outlet />
    </CentredLayoutWithHeader>
  );
}
